import { styled } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import MuiOutlinedInput from '@mui/material/OutlinedInput';

type IProps = {
  length: number;
  onComplete: (data: string) => void;
  onChange: (data: string) => void;
  onError: string;
  dataTestIdName?: string;
  direction?: 'rtl' | 'ltr';
};

const PinCode = ({ length, onComplete, onChange, onError, dataTestIdName, direction }: IProps) => {
  const inputBoxArr = useMemo(() => {
    return createInputBoxArr(length, onError);
  }, [length, onError]);

  inputBoxArr[0].autoFocus = true;

  const inputsRef = useRef<Array<HTMLInputElement>>([]);
  const [data, setData] = useState('');

  const reset = useCallback(() => {
    inputsRef.current.forEach(i => (i.value = ''));
    inputsRef.current[0].focus();
    inputBoxArr.forEach(element => (element.error = true));
    setData('');
  }, [inputBoxArr]);

  useEffect(() => {
    if (data.length === length) {
      onComplete(data);
    }
    onChange(data);
  }, [data, length]);

  useEffect(() => {
    if (onError) {
      reset();
    }
  }, [onError, reset]);

  const setValues = useCallback(() => {
    let value = '';
    inputsRef.current.forEach(i => (value += i.value));
    inputBoxArr.forEach(element => (element.error = false));
    setData(value);
  }, []);

  const handleOnChange = useCallback(
    (id: number, e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;

      //for case is user paste from clipboard
      if (val.length === length && id === 0) {
        inputsRef.current.forEach((input, index) => (input.value = val[index]));
        inputsRef.current[length - 1].focus();
        setValues();
        return;
      }

      //for case value length greater than one
      if (val.length > 1) {
        inputsRef.current[id].value = val[val.length - 1];
      }

      if (val && id < length - 1) {
        inputsRef.current[id + 1].focus();
      }
      setValues();
    },
    [setValues]
  );

  const handleOnKeyDown = useCallback(
    (id: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      const { key } = e;
      const target = e.target as HTMLInputElement;
      if (key === 'Backspace' && target.value === '' && id > 0) {
        inputsRef.current[id - 1].value = '';
        inputsRef.current[id - 1].focus();
        e.preventDefault();
      }
      setValues();
    },
    [setValues]
  );

  return (
    <InputsBoxStyled
      sx={{
        direction: direction,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      {inputBoxArr.map(item => (
        <MuiOutlinedInput
          key={item.id}
          autoFocus={item.autoFocus}
          inputRef={(el: any) => (inputsRef.current[item.id] = el)}
          size="small"
          color="primary"
          type="number"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            handleOnChange(item.id, e);
          }}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            handleOnKeyDown(item.id, e);
          }}
          inputProps={{ maxLength: 1, 'data-testid': `pin-code-input-${item.id}` }}
          error={item.error}
          sx={theme => ({
            //  width: '48px',
            width: {
              xs: '40px',
              sm: '48px'
            },
            height: {
              xs: '40px',
              sm: '48px'
            },

            input: {
              textAlign: 'center',
              typography: 'body1',
              color: 'secondary.main',
              height: '30px',
              '&[type=number]': {
                ' MozAppearance': 'textfield'
              },
              '&::-webkit-outer-spin-button': {
                WebkitAppearance: 'none',
                margin: 0
              },
              '&::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
                margin: 0
              },
              '&:-webkit-autofill': {
                WebkitBoxShadow: `0 0 0 100px ${theme.palette.background.default} inset`
              }
            },
            fieldset: {
              legend: {
                width: 'unset'
              }
            }
          })}
        />
      ))}
    </InputsBoxStyled>
  );
};

const createInputBoxArr = (length: number, onError: string) => {
  const arr = [];
  for (let i = 0; i < length; i++) {
    arr.push({ id: i, autoFocus: false, error: !!onError });
  }
  return arr;
};

const InputsBoxStyled = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: 'row',
  gap: theme.spacing(2)
}));

export default PinCode;
